import React, { useState } from 'react'
import { FileText, CheckCircle, Loader2, AlertCircle } from 'lucide-react'

interface PFASOptInFormProps {
  enrollmentData: any
  updateEnrollmentData: (field: string, value: any) => void
  handleNavigate: (stepIndex: number) => Promise<void>
  isNavigating?: boolean
}

export default function PFASOptInForm({
  enrollmentData,
  updateEnrollmentData,
  handleNavigate,
  isNavigating = false
}: PFASOptInFormProps) {
  const [formData, setFormData] = useState({
    waterExposure: enrollmentData.pfasWaterExposure || '',
    zipcode: enrollmentData.pfasZipcode || enrollmentData.zipcode || '',
    diagnosisType: enrollmentData.pfasDiagnosisType || '',
    victimRelation: enrollmentData.pfasVictimRelation || '',
    firstName: enrollmentData.firstName || '',
    lastName: enrollmentData.lastName || '',
    phone: enrollmentData.phone || '',
    email: enrollmentData.email || '',
    consentAccepted: enrollmentData.pfasConsentAccepted || false
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '')

    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
    }
  }

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value)
    handleInputChange('phone', formatted)
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.waterExposure) {
      newErrors.waterExposure = 'Please select an answer'
    }

    if (!formData.zipcode || formData.zipcode.length !== 5) {
      newErrors.zipcode = 'Please enter a valid 5-digit zipcode'
    }

    if (!formData.diagnosisType) {
      newErrors.diagnosisType = 'Please select a diagnosis type'
    }

    if (!formData.victimRelation) {
      newErrors.victimRelation = 'Please select who was diagnosed'
    }

    if (!formData.firstName || formData.firstName.trim().length < 2) {
      newErrors.firstName = 'Please enter your first name'
    }

    if (!formData.lastName || formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Please enter your last name'
    }

    if (!formData.phone || formData.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'Please enter a valid phone number'
    }

    if (!formData.email || !formData.email.includes('@')) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.consentAccepted) {
      newErrors.consentAccepted = 'You must accept the terms to continue'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      // Save form data to enrollment
      updateEnrollmentData('pfasWaterExposure', formData.waterExposure)
      updateEnrollmentData('pfasZipcode', formData.zipcode)
      updateEnrollmentData('pfasDiagnosisType', formData.diagnosisType)
      updateEnrollmentData('pfasVictimRelation', formData.victimRelation)
      updateEnrollmentData('firstName', formData.firstName)
      updateEnrollmentData('lastName', formData.lastName)
      updateEnrollmentData('phone', formData.phone)
      updateEnrollmentData('email', formData.email)
      updateEnrollmentData('pfasConsentAccepted', formData.consentAccepted)
      updateEnrollmentData('pfasOptInCompleted', true)
      updateEnrollmentData('pfasOptInCompletedAt', new Date().toISOString())

      // Navigate to next step
      await handleNavigate(2)
    } catch (error) {
      console.error('Error submitting opt-in form:', error)
      alert('There was an error submitting the form. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-accent-900">PFAS Opt-In Form</h2>
            <p className="text-sm text-accent-600">Complete the form below to opt-in to the PFAS lawsuit</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Water Exposure Question */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
            <label className="block text-sm font-semibold text-accent-800 mb-3">
              Were you or a loved one exposed to contaminated drinking water? *
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 bg-white rounded-lg border-2 border-gray-200 cursor-pointer hover:border-blue-400 transition-colors">
                <input
                  type="radio"
                  name="waterExposure"
                  value="yes"
                  checked={formData.waterExposure === 'yes'}
                  onChange={(e) => handleInputChange('waterExposure', e.target.value)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-gray-900 font-medium">Yes</span>
              </label>
              <label className="flex items-center gap-3 p-3 bg-white rounded-lg border-2 border-gray-200 cursor-pointer hover:border-blue-400 transition-colors">
                <input
                  type="radio"
                  name="waterExposure"
                  value="no"
                  checked={formData.waterExposure === 'no'}
                  onChange={(e) => handleInputChange('waterExposure', e.target.value)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-gray-900 font-medium">No</span>
              </label>
            </div>
            {errors.waterExposure && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.waterExposure}
              </p>
            )}
          </div>

          {/* Zipcode */}
          <div>
            <label className="block text-sm font-semibold text-accent-800 mb-2">
              Zip/Postal Code *
            </label>
            <input
              type="text"
              value={formData.zipcode}
              onChange={(e) => handleInputChange('zipcode', e.target.value.replace(/\D/g, '').slice(0, 5))}
              placeholder="Enter your zipcode"
              maxLength={5}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.zipcode ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.zipcode && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.zipcode}
              </p>
            )}
          </div>

          {/* Diagnosis Type */}
          <div>
            <label className="block text-sm font-semibold text-accent-800 mb-2">
              What type of cancer was diagnosed? *
            </label>
            <select
              value={formData.diagnosisType}
              onChange={(e) => handleInputChange('diagnosisType', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.diagnosisType ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select cancer type</option>
              <option value="kidney">Kidney Cancer</option>
              <option value="testicular">Testicular Cancer</option>
            </select>
            {errors.diagnosisType && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.diagnosisType}
              </p>
            )}
          </div>

          {/* Victim Relation */}
          <div>
            <label className="block text-sm font-semibold text-accent-800 mb-2">
              Who was diagnosed? *
            </label>
            <select
              value={formData.victimRelation}
              onChange={(e) => handleInputChange('victimRelation', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.victimRelation ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select an option</option>
              <option value="myself">Myself</option>
              <option value="loved_one">A loved one</option>
            </select>
            {errors.victimRelation && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.victimRelation}
              </p>
            )}
          </div>

          {/* First Name */}
          <div>
            <label className="block text-sm font-semibold text-accent-800 mb-2">
              First Name *
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              placeholder="Enter your first name"
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.firstName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.firstName && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.firstName}
              </p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-semibold text-accent-800 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              placeholder="Enter your last name"
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.lastName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.lastName && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.lastName}
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-semibold text-accent-800 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="(555) 123-4567"
              maxLength={14}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.phone ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.phone && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.phone}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-accent-800 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value.toLowerCase().trim())}
              placeholder="your.email@example.com"
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.email && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.email}
              </p>
            )}
          </div>

          {/* Consent Checkbox */}
          <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.consentAccepted}
                onChange={(e) => handleInputChange('consentAccepted', e.target.checked)}
                className="w-5 h-5 mt-1 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                I acknowledge that by clicking "Submit", I am providing my electronic signature and express written consent to be contacted by Napoli Shkolnik PLLC, their representatives, and affiliated law firms at the telephone number(s) and email address I have provided, for marketing purposes, including through the use of automated technology. I understand that my consent is not required as a condition of purchasing any property, goods, or services.
              </span>
            </label>
            {errors.consentAccepted && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.consentAccepted}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={isSubmitting || isNavigating}
              className="px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-semibold text-lg shadow-lg"
            >
              {isSubmitting || isNavigating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Submit & Continue</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

